// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview One-time migration that promotes existing TeamMember records
 * to RoleMembership records in the new RBAC system.
 *
 * Idempotent — safe to run multiple times. Members who already have a
 * matching RoleMembership are skipped. Per-member errors are accumulated
 * in the returned {@link MigrationResult.errors} array rather than
 * aborting the whole run.
 *
 * Pre-condition: {@link seedBuiltInRoles} must have been called for the
 * org before this migration runs.
 */

import { createId } from '../utils/id'
import type { TeamMemberModel } from '../models/TeamMember'
import type { RoleModel } from '../models/Role'
import type { RoleMembershipModel } from '../models/RoleMembership'

export interface MigrationDependencies {
  orgId: string
  systemUserId: string
  getTeamMembers(): Promise<TeamMemberModel[]>
  findRoleByName(name: string): Promise<Pick<RoleModel, 'id' | 'name'> | null>
  existsMembership(userId: string, roleId: string, orgId: string): Promise<boolean>
  insertMembership(membership: RoleMembershipModel): Promise<void>
}

export interface MigrationResult {
  migrated: number
  skipped: number
  errors: Array<{ userId: string; reason: string }>
}

/**
 * One-time migration: for each teamMember, create a rolemembership for their
 * org-level role if one does not already exist.
 *
 * Idempotent — safe to run multiple times. Already-migrated members are skipped.
 *
 * All side effects are injected via `deps` so this function is fully testable
 * without a live database. The caller is responsible for supplying correct
 * collection accessors scoped to the target org.
 *
 * @param deps - Injected dependencies (collections + context IDs)
 * @returns Counts of migrated, skipped, and errored members
 */
export async function migrateTeamMembersToRoleMemberships(
  deps: MigrationDependencies
): Promise<MigrationResult> {
  const { orgId, systemUserId } = deps
  let migrated = 0
  let skipped = 0
  const errors: MigrationResult['errors'] = []

  const members = await deps.getTeamMembers()

  for (const member of members) {
    if (!member.userId) {
      errors.push({ userId: '', reason: `TeamMember ${member.id ?? 'unknown'} missing userId` })
      continue
    }
    try {
      const role = await deps.findRoleByName(member.role)
      if (!role) {
        errors.push({ userId: member.userId, reason: `Built-in role '${member.role}' not found` })
        continue
      }

      const alreadyExists = await deps.existsMembership(member.userId, role.id, orgId)
      if (alreadyExists) {
        skipped++
        continue
      }

      const now = new Date().toISOString()
      await deps.insertMembership({
        id: createId('rolememberships'),
        roleId: role.id,
        userId: member.userId,
        grantedBy: systemUserId,
        orgId,
        scopeType: '',
        scopeId: '',
        expiresAt: '',
        status: 'active',
        createdAt: now,
        updatedAt: now,
      })
      migrated++
    } catch (err) {
      errors.push({ userId: member.userId, reason: String(err) })
    }
  }

  return { migrated, skipped, errors }
}
